import React, { useState, useRef, useEffect, useCallback } from 'react';

import { Card, Button } from 'antd';
import PageContainer from '../../../CSIComponents/PageContainer';
import { Grid } from '@material-ui/core';
import ImputComponent from '@components/InputComponent';
import SearchIcon from '@material-ui/icons/Search';
import SearchDebounceBar from '@components/SearchDebounce';
import { PropertySearch } from '@factories/propertySearchs';
import Table from './Table';
import AccessibilitySecurityModal from './Modals/AccessibilitySecurityModal';
import { getAmenityservices } from '@services/amenityservice';
import { LocalPersistence } from '@factories/index';
import { sortHelper } from '@helpers/misc/sortHelper';
import {
  paginationSize,
  getPaginationSession,
} from '@helpers/misc/setPaginationSize';

interface propsTypes {
  isEditable?: void | any;
  cancelEditable?: void | any;
  disableTabPanes?: void | any;
  detail?: any;
  cancelAndSave?: void | any;
  tableCurrentData?: any;
  scheduleAndChargeData: {
    amsid: number;
    amslangid: number;
    name: string;
    security: {
      accsecurity: string;
      charge: number;
      currency: string;
      description: string;
      quantity: number;
      securityid: number;
      securitylangid: number;
      type: string;
      typedetail: string;
      uom: string;
    }[];
  };
  editData?: void | any;
  handelCancelBtnClick?: void | any;
  setDisabledFlag?: any;
}

const columns = [
  {
    title: '',
    key: 'index',
    render: (text: string, record: [], index: number) => (index += 1),
    width: '40px',
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    sorter: (a: [], b: []) => sortHelper(a, b, 'type'),
    showSorterTooltip: false,
  },
  {
    title: 'Accessibility/Security',
    dataIndex: 'accsecurity',
    key: 'accsecurity',
    sorter: (a: [], b: []) => sortHelper(a, b, 'accsecurity'),
    showSorterTooltip: false,
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    sorter: (a: [], b: []) => sortHelper(a, b, 'quantity'),
    showSorterTooltip: false,
  },
  {
    title: 'Charge',
    dataIndex: 'charge',
    key: 'charge',
    sorter: (a: [], b: []) => sortHelper(a, b, 'charge'),
    showSorterTooltip: false,
  },
  {
    title: 'Type Detail',
    dataIndex: 'typedetail',
    key: 'typedetail',
    sorter: (a: [], b: []) => sortHelper(a, b, 'typedetail'),
    showSorterTooltip: false,
  },
];

const AccessibilityAndSecurity = ({
  scheduleAndChargeData,
  cancelAndSave,
  tableCurrentData,
}: propsTypes) => {
  const getData = () => {
    if (scheduleAndChargeData.security.length <= 1) {
      const [dt] = scheduleAndChargeData.security.map((data) =>
        data.hasOwnProperty('securityid')
      );

      if (!dt) return [];
    }

    return scheduleAndChargeData?.security;
  };
  const [editFlag, setEditFlag] = useState(false);
  const initialRender = useRef(true);
  const [searchs, setSearch] = useState<string[]>([]);
  const [dataSource, setDataSource] = useState<any[]>(getData());
  const [amenityData, setAmenityData] = useState(scheduleAndChargeData);
  const [total, setTotal] = useState(dataSource.length);
  const [data, setData] = useState<object[]>(scheduleAndChargeData.security);
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState([]);
  const [paginationData, setPaginationData] = useState<number | any>(null);

  const onChangeSearch = (term: string) => {
    setSearchTerm(term);
    onDebounce(term);
  };
  const { searchTerm, setSearchTerm, searchTermDebounced, onDebounce } =
    SearchDebounceBar();

  const recentlySearchs = () => {
    return searchs.map((search, index) => {
      return (
        <span
          key={index}
          onClick={() => recoverSearch(search)}
        >
          &nbsp;{search}
          {searchs.length - 1 === index ? '.' : ','}
        </span>
      );
    });
  };

  const recoverSearch = (search: string) => {
    setSearchTerm(search);
    filterData(search);
    onDebounce(search);
  };

  const saveNewSearch = (term: string) => {
    const stackSearchs = new PropertySearch(searchs);
    setSearch(stackSearchs.add(term));
  };

  const afterDebounce = (term: string) => {
    if (term) {
      saveNewSearch(term);
    }
    filterData(term);
  };

  const filterData = async (query: string) => {
    const filterData = data.filter((item: any) => {
      const newFilterData: any = {};
      columns.forEach((col: any) => {
        newFilterData[col['dataIndex']] =
          item[col.dataIndex] == null || item[col.dataIndex] == undefined
            ? ''
            : JSON.stringify(item[col.dataIndex]).toLowerCase();
      });
      return (
        JSON.stringify(Object.values(newFilterData)).indexOf(
          query.toLowerCase()
        ) > -1
      );
    });

    setDataSource(filterData);
  };

  const refreshAmenitie = useCallback(async () => {
    const get_session = new LocalPersistence('session');
    const session: any = await get_session.getSessionPersistence();
    const res = await getAmenityservices(
      session?.supplier,
      session?.propertyId
    );

    const [amenity] = res?.property?.amenityservice.filter(
      ({ amsid, amslangid }: any) =>
        amsid === scheduleAndChargeData.amsid &&
        amslangid === scheduleAndChargeData?.amslangid
    );
    setDataSource(amenity.security);
    setAmenityData(amenity);
  }, [scheduleAndChargeData.amsid, scheduleAndChargeData?.amslangid]);

  const getPaginationData = useCallback(async () => {
    return await getPaginationSession(
      setPaginationData,
      'accesibility_and_security_table'
    );
  }, []);

  useEffect(() => {
    getPaginationData();
  }, []);

  useEffect(
    () => {
      const dataSourceLength = dataSource.length;
      setTotal(dataSourceLength);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSource]
  );

  //   search functionality
  useEffect(
    () => {
      if (initialRender.current) {
        initialRender.current = false;
      } else {
        afterDebounce(searchTermDebounced);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTermDebounced]
  );

  return (
    <>
      <PageContainer>
        <Card style={{ marginBottom: '60px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 'bold' }}>
              {scheduleAndChargeData?.name}
            </div>
            <div style={{ display: 'flex' }}>
              <div style={{ margin: '0 10px 0 0' }}>
                <Button
                  type="primary"
                  className="db-edit-btn"
                  onClick={() => cancelAndSave()}
                >
                  Back
                </Button>
              </div>
              <div>
                <Button
                  type="primary"
                  className="db-edit-btn"
                  onClick={() => {
                    setCurrentRow([]);
                    setVisible(true);
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
          <Grid
            container
            spacing={0}
            className="containerSpacing py-0"
          >
            <Grid
              item
              xs={4}
              className="pb-0 px-0 my-0"
            >
              <ImputComponent
                className="SearchBar"
                //label={lang.translate(language, 'search')}
                label={'Search'}
                labelShrink={true}
                value={searchTerm}
                onChange={onChangeSearch}
                placeholder="Search Accessibility/Security"
                endAdornment={<SearchIcon style={{ color: '#9a9a9a' }} />}
              />
              <div className="recentlySearchs">
                <p>{recentlySearchs()}</p>
              </div>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            className="p-0"
          >
            {paginationData && (
              <Table
                columns={columns}
                data={dataSource}
                setVisible={setVisible}
                setCurrentRow={setCurrentRow}
                paginationSize={paginationSize}
                paginationData={paginationData}
              />
            )}
          </Grid>
        </Card>
        <AccessibilitySecurityModal
          onClose={() => setVisible(false)}
          visible={visible}
          setCurrentRow={setCurrentRow}
          currentRow={currentRow}
          name={scheduleAndChargeData?.name}
          setDataSource={setDataSource}
          tableCurrentData={amenityData}
          refreshAmenitie={refreshAmenitie}
        />
      </PageContainer>
    </>
  );
};

export default AccessibilityAndSecurity;
